// 週間AIダイジェスト print template — A4 layout, ja/en versions.
import { formatJSTLocale } from '@/utils/datetime';
import { jstDateTimeShort } from '@/utils/jstDate';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (ch) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));

// AIの応答が配列でなく文字列で返ってくるケースにも耐える正規化
const asArr = (v) => {
  if (Array.isArray(v)) return v;
  if (v === null || v === undefined || v === '') return [];
  if (typeof v === 'string') return v.split(/\n|(?: \/ )|、|・/).map((s) => s.trim()).filter(Boolean);
  return [v];
};

const LBL = {
  ja: {
    title: '週間AIダイジェスト', subtitle: '経営向けサマリー', period: '対象期間',
    reports: '日報', engineers: 'エンジニア', projects: 'プロジェクト',
    unitReports: '件', unitPeople: '名', unitProjects: '件',
    execSummary: 'エグゼクティブサマリー', accomplishments: '主な成果',
    activeProjects: '進行中のプロジェクト', risks: 'リスクの兆候', blocked: 'ブロック中の活動',
    lessons: '教訓・学び', upcoming: '来週の予定', themes: '戦略テーマ',
    engineerSummaries: 'エンジニア別サマリー', projectSummaries: 'プロジェクト別サマリー',
    projLabel: 'プロジェクト', done: '成果', blockers: 'ブロッカー', nextActions: '次のアクション',
    completed: '完了', ongoing: '進行中', nextWeek: '来週',
    aiFeedback: 'AIアシスタント フィードバック', aiFbHint: 'AIが答えられなかった質問（低評価 👎）',
    aiFbNone: '今週の低評価はありません', aiFbQ: '質問', aiFbA: '回答（抜粋）',
    generatedBy: '生成者', generatedAt: '生成日時', footer: 'Sensbey Web App（統合業務プラットフォーム）— 技術 / 週間AIダイジェストより自動生成',
  },
  en: {
    title: 'Weekly AI Digest', subtitle: 'Management Summary', period: 'Period',
    reports: 'Reports', engineers: 'Engineers', projects: 'Projects',
    unitReports: '', unitPeople: '', unitProjects: '',
    execSummary: 'Executive Summary', accomplishments: 'Key Accomplishments',
    activeProjects: 'Active Projects', risks: 'Risk Signals', blocked: 'Blocked Activities',
    lessons: 'Lessons Learned', upcoming: 'Upcoming (Next Week)', themes: 'Strategic Themes',
    engineerSummaries: 'Per-Engineer Summary', projectSummaries: 'Per-Project Summary',
    projLabel: 'Projects', done: 'Accomplishments', blockers: 'Blockers', nextActions: 'Next actions',
    completed: 'Completed', ongoing: 'Ongoing', nextWeek: 'Next week',
    aiFeedback: 'AI Assistant Feedback', aiFbHint: 'Questions the AI could not answer well (rated 👎)',
    aiFbNone: 'No low-rated answers this week', aiFbQ: 'Q', aiFbA: 'A (excerpt)',
    generatedBy: 'Generated by', generatedAt: 'Generated at', footer: 'Sensbey Web App (Business Platform) — Engineering / Weekly AI Digest (auto-generated)',
  },
};

const ul = (items) => {
  const arr = asArr(items);
  return arr.length === 0 ? '<p class="none">—</p>'
    : `<ul>${arr.map((x) => `<li>${esc(x)}</li>`).join('')}</ul>`;
};

const kv = (label, items, cls = '') => {
  const arr = asArr(items);
  return arr.length === 0 ? ''
    : `<div class="kv"><span class="k ${cls}">${esc(label)}:</span> ${arr.map(esc).join(' / ')}</div>`;
};

export function buildDigestPrintHtml(detail, lang = 'ja', version = null) {
  const L = LBL[lang] || LBL.ja;
  // Stage F: which immutable version is being printed (active / historical / candidate)
  const vLife = version?.lifecycle;
  const vLabel = !version ? '' : lang === 'en'
    ? `v${version.version_number} · ${{ active: 'ACTIVE', superseded: 'HISTORICAL (not active)', candidate: 'CANDIDATE (not active)' }[vLife] || vLife}`
    : `v${version.version_number} · ${{ active: '有効版', superseded: '履歴（有効版ではありません）', candidate: '候補（有効版ではありません）' }[vLife] || vLife}`;
  const vBanner = version && vLife !== 'active'
    ? `<div class="version-warning" data-testid="print-version-warning" style="border:2px solid #4f46e5;background:#eef2ff;color:#312e81;padding:8px 14px;margin:10px 0 12px;border-radius:8px;font-size:12px;"><strong>${esc(lang === 'en' ? 'This is not the active digest.' : 'これは有効版のダイジェストではありません。')}</strong> ${esc(vLabel)}</div>`
    : '';
  const en = detail.english_version || {};
  const useEn = lang === 'en' && detail.english_version;
  const mgmt = (useEn ? en.management_digest : detail.management_digest) || {};
  const engSums = asArr(useEn ? en.engineer_summaries : detail.engineer_summaries);
  const projSums = asArr(useEn ? en.project_summaries : detail.project_summaries);

  const mgmtSections = [
    ['accomplishments', L.accomplishments], ['active_projects', L.activeProjects],
    ['risks', L.risks], ['blocked', L.blocked], ['lessons', L.lessons], ['upcoming', L.upcoming],
  ].map(([k, label]) => `<div class="sec"><h3>${esc(label)}</h3>${ul(mgmt[k])}</div>`).join('');

  const themesArr = asArr(mgmt.themes);
  const themes = themesArr.length > 0
    ? `<div class="themes"><h3>${esc(L.themes)}</h3><p>${themesArr.map((t) => `<span class="chip">${esc(t)}</span>`).join(' ')}</p></div>` : '';

  const engRows = engSums.map((e) => `
    <div class="card">
      <p class="name">${esc(e.engineer)}</p>
      ${asArr(e.projects).length > 0 ? `<p class="sub">${esc(L.projLabel)}: ${asArr(e.projects).map(esc).join(', ')}</p>` : ''}
      ${kv(L.done, e.accomplishments, 'g')}${kv(L.blockers, e.blockers, 'r')}${kv(L.nextActions, e.next_actions, 'b')}
    </div>`).join('');

  const projRows = projSums.map((p) => `
    <div class="card">
      <p class="name">${esc(p.project)}</p>
      ${kv(L.completed, p.completed, 'g')}${kv(L.ongoing, p.ongoing, 'b')}${kv(L.blockers, p.blockers, 'r')}${kv(L.nextWeek, p.next_week, 'v')}
    </div>`).join('');

  const fb = detail.ai_feedback;
  const fbLowRated = asArr(fb?.low_rated);
  const fbSection = fb ? `
<h2>${esc(L.aiFeedback)}（👍 ${esc(fb.up)} / 👎 ${esc(fb.down)}）</h2>
<p class="sub" style="margin:0 0 6px;color:#71717a;font-size:10.5px;">${esc(L.aiFbHint)}</p>
${fbLowRated.length === 0 ? `<p class="none">${esc(L.aiFbNone)}</p>`
    : fbLowRated.map((r) => `
    <div class="card">
      <p class="name">${esc(L.aiFbQ)}: ${esc(r.question || '—')}</p>
      <p class="sub">${esc(L.aiFbA)}: ${esc((r.answer_preview || '').slice(0, 160))}</p>
      <p class="sub">${esc(jstDateTimeShort(r.ts))} · ${esc(r.user_name || '')}</p>
    </div>`).join('')}` : '';

  return `<!DOCTYPE html><html lang="${lang}"><head><meta charset="utf-8">
<title>${esc(L.title)} ${esc(detail.week_start)} - ${esc(detail.week_end)}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Hiragino Kaku Gothic ProN", "Yu Gothic", "Segoe UI", sans-serif;
         color: #18181b; margin: 0; font-size: 11.5px; line-height: 1.55; }
  header { display: flex; justify-content: space-between; align-items: baseline;
           border-bottom: 3px double #6d28d9; padding-bottom: 6px; margin-bottom: 10px; }
  header h1 { margin: 0; font-size: 19px; letter-spacing: 0.5px; }
  header .brand { font-size: 11px; color: #71717a; letter-spacing: 1px; }
  .meta { display: flex; gap: 18px; flex-wrap: wrap; font-size: 11px; background: #f4f4f5;
          padding: 7px 12px; border-radius: 6px; margin-bottom: 12px; }
  .meta b { color: #52525b; font-weight: 600; margin-right: 4px; }
  h2 { font-size: 13px; color: #5b21b6; border-left: 4px solid #6d28d9; padding-left: 8px;
       margin: 14px 0 8px; letter-spacing: 0.5px; }
  h3 { font-size: 11px; color: #3f3f46; margin: 0 0 3px; text-transform: uppercase; letter-spacing: 0.6px; }
  .exec { background: #f5f3ff; border: 1px solid #ddd6fe; border-radius: 6px; padding: 9px 12px; margin-bottom: 10px; }
  .exec p { margin: 0; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 16px; }
  .sec ul { margin: 0; padding-left: 16px; }
  .sec li { margin-bottom: 2px; }
  .none { color: #a1a1aa; margin: 0; }
  .themes { margin-top: 10px; }
  .themes p { margin: 2px 0 0; }
  .chip { display: inline-block; border: 1px solid #c7d2fe; background: #eef2ff; color: #4338ca;
          border-radius: 999px; padding: 1px 8px; font-size: 10px; font-weight: 600; margin: 0 3px 3px 0; }
  .card { border: 1px solid #e4e4e7; border-radius: 6px; padding: 7px 10px; margin-bottom: 7px;
          break-inside: avoid; background: #fafafa; }
  .card .name { margin: 0; font-weight: 700; font-size: 12px; }
  .card .sub { margin: 1px 0 0; color: #71717a; font-size: 10.5px; }
  .kv { margin-top: 2px; }
  .kv .k { font-weight: 600; }
  .kv .k.g { color: #047857; } .kv .k.r { color: #e11d48; } .kv .k.b { color: #0369a1; } .kv .k.v { color: #6d28d9; }
  .footer { margin-top: 16px; text-align: right; font-size: 9.5px; color: #a1a1aa; }
  @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
</style></head>
<body>
<header>
  <h1>${esc(L.title)}（${esc(detail.week_start)} 〜 ${esc(detail.week_end)}）</h1>
  ${detail.status === 'complete_with_warnings' && detail.user_warning ? `<div class="partial-warning" data-testid="print-partial-warning" style="border:2px solid #d97706;background:#fffbeb;color:#78350f;padding:10px 14px;margin:10px 0 16px;border-radius:8px;font-size:12px;white-space:pre-wrap;"><strong>${esc(useEn ? 'Some sections could not be generated — not management-ready' : '一部のセクションを生成できませんでした（管理資料としては未完成）')}</strong>\n${esc(detail.user_warning)}\n${esc(useEn ? 'Failed sections: ' : '未生成セクション: ')}${esc((detail.incomplete_sections || []).map((k) => ({ engineer: 'エンジニア別サマリー', project: 'プロジェクト別サマリー', management: '経営向けサマリー' }[k] || k) + ((detail.partial_sections || {})[k] ? (useEn ? ' (partial)' : '（一部）') : '')).join('、'))}</div>` : ''}
  <div class="brand">SENSBEY ／ ${lang === 'en' ? 'ENGINEERING' : '技術'}</div>
</header>
<div class="meta">
  <span><b>${esc(L.period)}</b>${esc(detail.week_start)} 〜 ${esc(detail.week_end)}</span>
  <span><b>${esc(L.reports)}</b>${esc(detail.report_count)}${esc(L.unitReports)}</span>
  <span><b>${esc(L.engineers)}</b>${esc(detail.engineer_count)}${esc(L.unitPeople)}</span>
  <span><b>${esc(L.projects)}</b>${esc(detail.project_count)}${esc(L.unitProjects)}</span>
  <span><b>${esc(L.generatedBy)}</b>${esc(detail.generated_by)}</span>
  ${version ? `<span data-testid="print-version-label"><b>${esc(lang === 'en' ? 'Version' : 'バージョン')}</b>${esc(vLabel)}</span>` : ''}
</div>
${vBanner}
<h2>${esc(L.subtitle)}</h2>
${mgmt.executive_summary ? `<div class="exec"><h3>${esc(L.execSummary)}</h3><p>${esc(mgmt.executive_summary)}</p></div>` : ''}
<div class="grid">${mgmtSections}</div>
${themes}
<h2>${esc(L.engineerSummaries)}</h2>
${engRows || '<p class="none">—</p>'}
<h2>${esc(L.projectSummaries)}</h2>
${projRows || '<p class="none">—</p>'}
${fbSection}
<p class="footer">${esc(L.footer)} · ${esc(formatJSTLocale(new Date()))}</p>
</body></html>`;
}
